import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PillButtonGroup from "../../components/PillButtonGroup";
import TherapistApplicationsTab from "./portalUsers/TherapistApplicationsTab";
import CustomersTab from "./portalUsers/CustomersTab";

const TABS = [
  { key: "therapists", label: "Therapists" },
  { key: "customers", label: "Customers" },
];

// Small, content-sized tab switcher for the page header's top-right corner
// - unlike WorkspaceTabs (used elsewhere), this never stretches full width.
function CompactHeaderTabs({ tabs, value, onChange }) {
  return (
    <PillButtonGroup sx={{ p: 0.25, flexShrink: 0 }}>
      {tabs.map((tab) => (
        <Button
          key={tab.key}
          variant="text"
          onClick={() => onChange(tab.key)}
          sx={{
            px: 2,
            py: 0.5,
            minHeight: 0,
            fontSize: "0.8125rem",
            fontWeight: value === tab.key ? 600 : 400,
            backgroundColor: value === tab.key ? "#EBEBEB" : "transparent",
            color: "#333",
            "&:hover": { backgroundColor: value === tab.key ? "#EBEBEB" : "#f5f5f5" },
          }}
        >
          {tab.label}
        </Button>
      ))}
    </PillButtonGroup>
  );
}

function PortalUsers() {
  // Tab lives in the URL (not local state) so that navigating into a
  // customer detail page and pressing back restores the tab that was
  // active - a plain useState here resets to the default on remount since
  // browser back re-mounts this route from scratch.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "customers" ? "customers" : "therapists";

  const handleTabChange = (tab) => {
    setSearchParams(tab === "therapists" ? {} : { tab }, { replace: true });
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          px: 1,
          py: 1,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PeopleAltIcon sx={{ fontSize: "1.5rem", color: "#333" }} />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            Portal Users
          </Typography>
        </Box>

        <CompactHeaderTabs tabs={TABS} value={activeTab} onChange={handleTabChange} />
      </Box>

      {/* Both tab bodies stay mounted so each tab's selection/search state
          survives switching back and forth. Therapists is a fixed-height
          master-detail pane that scrolls internally; Customers is a plain
          table that should grow with its content and let the page itself
          scroll, same as the Orders page. */}
      <Box sx={{ display: activeTab === "therapists" ? "flex" : "none", flex: 1, minHeight: 0, px: 1, pb: 1 }}>
        <TherapistApplicationsTab />
      </Box>
      <Box sx={{ display: activeTab === "customers" ? "block" : "none", px: 1, pb: 1 }}>
        <CustomersTab />
      </Box>
    </Box>
  );
}

export default PortalUsers;
